'use client';

import { ReactNode } from 'react';
import { useCandidateNavigationOptional, type CandidateSearchFilters } from '@components/contexts/CandidateNavigationContext';
import { cn } from '@utils/cn';
import { PromptBarShell } from './PromptBarShell';
import { PromptBarScrim } from './PromptBarScrim';
import { PromptBarActionsPopup } from './PromptBarActionsPopup';
import { PromptBarChips } from './PromptBarChips';
import { SearchBranch } from './SearchBranch';
import { ChatBranch } from './ChatBranch';
import { MissionChipsRow } from './MissionChipsRow';
import { usePromptBarMode, type PromptBarMode } from './usePromptBarMode';

interface CandidatePromptBarProps {
  locale: Locale;
  isAuthenticated?: boolean;
  /** Force a mode. Otherwise derived from currentView via usePromptBarMode. */
  modeOverride?: PromptBarMode;
  /** Chat submit handler. Required for chat mode to function; if absent the
   *  branch still renders but the user cannot send a message. */
  onChatSubmit?: (text: string) => Promise<void>;
  /** Search submit handler. Defaults to navigateToCandidateSearch. */
  onSearchSubmit?: (filters: CandidateSearchFilters) => void;
  /** Render in document flow instead of fixed-position overlay. */
  inline?: boolean;
  /** Pin to the viewport bottom while scrolling, then dock in-flow at the end of the
   *  page. Wrapper is `sticky`; the shell renders in-flow. */
  sticky?: boolean;
  /** Extra content rendered under the mission chips (e.g. a legal bar). Pins/docks
   *  together with the bar. */
  belowChipsSlot?: ReactNode;
  /** Render just the mission chips instead of the full prompt bar. Used on
   *  surfaces where the input is not useful (company pages). */
  collapsedFab?: boolean;
}

const noopChatSubmit: (text: string) => Promise<void> = async () => {};

export const CandidatePromptBar = ({
  locale,
  isAuthenticated = false,
  modeOverride,
  onChatSubmit,
  onSearchSubmit,
  inline = false,
  sticky = false,
  belowChipsSlot,
  collapsedFab = false,
}: CandidatePromptBarProps) => {
  const mode = usePromptBarMode(modeOverride);
  const candidateNav = useCandidateNavigationOptional();
  const handleSearchSubmit = onSearchSubmit ?? candidateNav?.navigateToCandidateSearch;

  // Company-page surfaces (collapsedFab): just the mission chips — no full prompt bar.
  if (collapsedFab) {
    return <PromptBarChips locale={locale} mode={mode} isAuthenticated={isAuthenticated} inline={inline} />;
  }

  const popup = <PromptBarActionsPopup locale={locale} mode={mode} isAuthenticated={isAuthenticated} />;

  return (
    <>
      {/* Sticky mode uses the in-flow shell (no built-in scrim), so paint the scrim here. */}
      {sticky && <PromptBarScrim />}
      <div className={cn('w-full', sticky && 'sticky bottom-3 z-30 px-1 lg:bottom-8 lg:px-2')}>
        <PromptBarShell
          inline={inline || sticky}
          belowSlot={(
            <>
              <MissionChipsRow locale={locale} mode={mode} isAuthenticated={isAuthenticated} className="mt-2" />
              {belowChipsSlot}
            </>
          )}
        >
          {mode === 'search' && handleSearchSubmit
            ? <SearchBranch locale={locale} onSubmit={handleSearchSubmit} leftSlot={popup} />
            : <ChatBranch onSubmit={onChatSubmit ?? noopChatSubmit} leftSlot={popup} />}
        </PromptBarShell>
      </div>
    </>
  );
};
